import React, { useState, useEffect, useRef } from 'react';
import Button from '@albertsons/uds/molecule/Button';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import getStopStatus from '../services/selfServiceServices/getStopStatus';
import Environment from '../utils/Environment';


interface EditableTableProps {
  headers: string[];
  data: string[][];
  onDataChange?: (newData: string[][]) => void;
  onCellEdit?: (rowIndex: number, columnName: string, value: string) => void;
  isLoading?: boolean;
  maxHeight?: string;
  mandatoryFields?: string[];
  fieldTypes?: { [fieldName: string]: 'string' | 'number' | 'decimal' | 'date' | 'boolean' };
  showEditControls?: boolean;
  actionButtons?: React.ReactNode;
  onValidate?: () => boolean;
  onValidationChange?: (hasErrors: boolean) => void;
  onSelectedRowsChange?: (selectedRows: Set<number>) => void;
  selectedGroup?: string; // Add to detect shipment reset context
  selectedSubGroup?: string; // Add to detect shipment reset context
  selectedOrg?: string; // DC code for API calls
  showPaginationControls?: boolean;
  nonEditableColumns?: string[]; // New prop for non-editable columns
  onCellClick?: (rowIndex: number, columnName: string) => void;
  validationErrors?: { [key: string]: string };
  isCellDisabled?: (rowIndex: number, columnName: string) => boolean;
  resetSelectionTrigger?: number;
  onReset?: () => void
}

interface Column {
  key: string;
  name: string;
}

const EditableTable: React.FC<EditableTableProps> = ({
  headers,
  data,
  onDataChange,
  onCellEdit,
  isLoading = false,
  maxHeight = '60vh',
  mandatoryFields = [],
  fieldTypes = {},
  showEditControls = true,
  actionButtons,
  onValidate,
  onValidationChange,
  onSelectedRowsChange,
  selectedGroup = '',
  selectedSubGroup = '',
  selectedOrg,
  showPaginationControls = true,
  nonEditableColumns = [],
  onCellClick,
  validationErrors: customValidationErrors,
  isCellDisabled,
  resetSelectionTrigger,
  onReset
}) => {
  // Memoize columns to prevent recreation on every render
  const columns: Column[] = React.useMemo(() => 
    headers.map((header, index) => ({
      key: `col_${index}`,
      name: header || `Column ${index + 1}`
    })), [headers]
  );

  // Convert data to rows with proper keys
  const [rows, setRows] = useState<any[]>([]);

  // Validation states
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>(customValidationErrors || {});
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [validationAlertMessage, setValidationAlertMessage] = useState('');

  useEffect(() => {
    setValidationErrors(customValidationErrors || {});
  }, [customValidationErrors]);

  // Snackbar states for error messages
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');

  // Debouncing refs for shipment status API calls
  const shipmentStatusTimerRef = React.useRef<{ [rowIndex: number]: ReturnType<typeof setTimeout> }>({});
  const shipmentStatusReqIdRef = React.useRef<{ [rowIndex: number]: number }>({});
  const shipmentStatusAbortRef = React.useRef<{ [rowIndex: number]: AbortController | null }>({});

  // Log when validation props change
  useEffect(() => {
  }, [mandatoryFields, fieldTypes, headers]);

  // Pagination states
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [jumpToPageValue, setJumpToPageValue] = useState('1');
  const [jumpToPageError, setJumpToPageError] = useState<string | null>(null);

  // Selection and editing states
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [editingCell, setEditingCell] = useState<{ row: number; col: string } | null>(null);
  
  useEffect(() => {
  setSelectedRows(new Set()); // ✅ clear checkboxes without remount
}, [resetSelectionTrigger]);


  // 🔹 Auto-select the row as soon as any cell enters edit mode
  useEffect(() => {
    if (editingCell) {
      const rowIndex = editingCell.row;
      setSelectedRows(prev => {
        const next = new Set(prev);
        next.add(rowIndex);          // select this row on entry
        return next;
      });
      // Parent will be notified by the existing onSelectedRowsChange effect
    }
  }, [editingCell]);


  // Drag selection states
  const [dragSelecting, setDragSelecting] = useState(false);
  const [dragStartCell, setDragStartCell] = useState<{ row: number; col: number } | null>(null);
  const [dragEndCell, setDragEndCell] = useState<{ row: number; col: number } | null>(null);
  const [selectedCellRect, setSelectedCellRect] = useState<{ rowMin: number; rowMax: number; colMin: number; colMax: number } | null>(null);

  const tableRef = useRef<HTMLTableElement>(null);

  // Validation function
  const validateCell = React.useCallback((rowIndex: number, colKey: string, value: string): string | null => {
    const colIndex = columns.findIndex(col => col.key === colKey);
    if (colIndex === -1) return null;
    
    const headerName = headers[colIndex];
    
    // Check if field is mandatory and empty
    if (mandatoryFields.includes(headerName) && (!value || value.trim() === '')) {
      // Special handling for Reset Status - only mandatory if Stop Status has valid value
      const normalizedHeaderName = headerName?.toLowerCase().replace(/[\s_-]/g, '');
      if (normalizedHeaderName === 'resetstatus') {
        // Find Stop Status column and check its value
        const stopStatusColIndex = headers.findIndex(header => 
          header?.toLowerCase().replace(/[\s_-]/g, '') === 'stopstatus'
        );
        if (stopStatusColIndex !== -1) {
          const stopStatusColKey = columns[stopStatusColIndex]?.key;
          const stopStatusValue = stopStatusColKey ? rows[rowIndex]?.[stopStatusColKey] : '';
          const hasValidStopStatus = stopStatusValue && 
            String(stopStatusValue).trim() !== '' && 
            String(stopStatusValue).trim() !== 'Error' && 
            String(stopStatusValue).trim().toLowerCase() !== 'unknown';
          
          // Only require Reset Status if Stop Status has a valid value
          if (hasValidStopStatus) {
            // If we're showing a dropdown for this cell, it should have a default value
            // Don't show validation error if the cell would have a default value
            return null; // Don't mark as error since dropdown will provide default value
          }
          // If Stop Status is unknown/empty/error, Reset Status is not mandatory
          return null;
        }
      }
      
      // Regular mandatory field validation for non-Reset Status fields
      return `${headerName} is a mandatory field`;
    }
    
    // Check field type - try exact match first, then normalized match
    let fieldType = fieldTypes[headerName];
    
    // If no exact match, try to find field type with normalized header name
    if (!fieldType) {
      const normalizedHeaderName = headerName?.toLowerCase().replace(/[\s_-]/g, '');
      const fieldTypeKey = Object.keys(fieldTypes).find(key => 
        key.toLowerCase().replace(/[\s_-]/g, '') === normalizedHeaderName
      );
      if (fieldTypeKey) {
        fieldType = fieldTypes[fieldTypeKey];
      }
    }
    
    // Special handling for Shipment ID - skip field type validation as it can be numeric, alphanumeric, or string
    const normalizedHeader = headerName?.toLowerCase().replace(/[\s_-]/g, '');
    if (normalizedHeader === 'shipmentid' || normalizedHeader === 'shipment') {
      return null; // Skip field type validation for ShipmentId
    }
    
    if (fieldType && value && value.trim() !== '') {
      const fieldTypeLower = fieldType.toLowerCase();
      
      switch (fieldTypeLower) {
        case 'string':
          if (/^\d+(\.\d+)?$/.test(value.trim())) {
            return `${headerName} must be a string (not just numbers)`;
          }
          break;
        case 'number':
        case 'integer':
          // Integer validation - no decimal points allowed
          if (isNaN(Number(value)) || value.includes('.') || value.includes(',')) {
            return `${headerName} must be a whole number (no decimals)`;
          }
          break;
        case 'decimal':
        case 'float':
        case 'double':
          // Decimal validation - allows decimal points
          if (isNaN(Number(value))) {
            return `${headerName} must be a number (decimals allowed)`;
          }
          break;
        case 'date':
          const dateValue = new Date(value);
          if (isNaN(dateValue.getTime())) {
            return `${headerName} must be a valid date`;
          }
          break;
        case 'boolean':
          const lowerValue = value.toLowerCase();
          if (!['true', 'false', '1', '0', 'yes', 'no'].includes(lowerValue)) {
            return `${headerName} must be a boolean value (true/false, yes/no, 1/0)`;
          }
          break;
      }
    }
    
    return null;
  }, [rows, columns, headers, mandatoryFields, fieldTypes]);

  // Validate all cells
  const validateAllCells = React.useCallback((): boolean => {
    const errors: { [key: string]: string } = {};
    let hasErrors = false;
    
    rows.forEach((row, rowIndex) => {
      const hasData = columns.some(col => {
        const value = row[col.key] || '';
        return value.trim() !== '';
      });

      if (hasData) {
        columns.forEach(col => {
          const value = row[col.key] || '';
          const error = validateCell(rowIndex, col.key, value);
          if (error) {
            errors[`${rowIndex}-${col.key}`] = error;
            hasErrors = true;
          }
        });
      }
    });
    
    // Preserve existing "Invalid Shipment Id" errors that were set manually
    const existingErrors = { ...validationErrors };
    Object.keys(existingErrors).forEach(errorKey => {
      if (existingErrors[errorKey] === 'Invalid Shipment Id') {
        errors[errorKey] = existingErrors[errorKey];
        // Don't count "Invalid Shipment Id" errors in the validation alert count
        // as they are handled by their own popup system
      }
    });
    
    setValidationErrors(errors);
    
    if (hasErrors) {
      const errorCount = Object.keys(errors).filter(key => errors[key] !== 'Invalid Shipment Id').length;
      if (errorCount > 0) {
        setValidationAlertMessage(`Found ${errorCount} validation error${errorCount > 1 ? 's' : ''}. Please fix the highlighted cells.`);
        setShowValidationAlert(true);
      }
    }
    
    return !hasErrors;
  }, [rows, columns, validateCell, validationErrors]);

   useEffect(() => {
    let convertedRows: any[];
    
    if (data.length === 0) {
      // Clear selected rows when data is cleared
      setSelectedRows(new Set());
      
      convertedRows = Array.from({ length: 25 }, (_, index: number) => {
        const rowObject: any = { tempKey: `row_${index}` };
        columns.forEach((col) => {
          // Don't set default values for reset status columns in empty tables
          // Only set defaults when there's actual data being processed
          rowObject[col.key] = '';
        });
        return rowObject;
      });
    } else {
      convertedRows = data.map((row, index: number) => {
        const rowObject: any = { tempKey: `row_${index}` };
        columns.forEach((col, colIndex) => {
          rowObject[col.key] = row[colIndex] || '';
        });
        return rowObject;
      });
      const currentLength = convertedRows.length;
      if (currentLength < 25) {
        const blankRows = Array.from({ length: 25 - currentLength }, (_, index: number) => {
          const rowObject: any = { tempKey: `row_${currentLength + index}` };
          columns.forEach((col) => {
            rowObject[col.key] = '';
          });
          return rowObject;
        });
        convertedRows = [...convertedRows, ...blankRows];
      }
    }
    
    // Only update if data actually changed
    setRows(prevRows => {
      if (prevRows.length !== convertedRows.length) {
        return convertedRows;
      }
      
      // Check if any data has actually changed
      const hasChanged = convertedRows.some((newRow: any, index: number) => {
        const oldRow = prevRows[index];
        if (!oldRow) return true;
        
        return columns.some(col => oldRow[col.key] !== newRow[col.key]);
      });
      
      return hasChanged ? convertedRows : prevRows;
    });
  }, [data, columns, headers]);

  // Auto-populate Stop Status for Shipment Reset context
  useEffect(() => {
    // Note: Removed automatic API calling on data load
    // Stop status will only be populated when user types/modifies shipment ID
    // This ensures all API calls use the same debounced logic (500ms delay)
    
  }, [rows, headers, columns, selectedSubGroup, selectedOrg]);



  // Update parent when data changes - memoized to prevent unnecessary updates
  const convertedDataRef = useRef<string[][]>([]);
  
  useEffect(() => {
  if (!onDataChange || rows.length === 0) return;

  const convertedData = rows.map(row =>
    columns.map(col => row[col.key] || '')
  );

  const hasChanged =
    convertedData.length !== convertedDataRef.current.length ||
    convertedData.some((row, rowIndex) =>
      row.some((cell, colIndex) =>
        cell !== (convertedDataRef.current[rowIndex]?.[colIndex] || '')
      )
    );

  if (!hasChanged) return;

  convertedDataRef.current = convertedData;
  onDataChange(convertedData);

}, [rows, onDataChange, columns]);

  // Helper functions
  const currentPage = paginationModel.page;

  const getSelectedCellRect = () => {
    if (!dragStartCell || !dragEndCell) return null;
    const rowMin = Math.min(dragStartCell.row, dragEndCell.row);
    const rowMax = Math.max(dragStartCell.row, dragEndCell.row);
    const colMin = Math.min(dragStartCell.col, dragEndCell.col);
    const colMax = Math.max(dragStartCell.col, dragEndCell.col);
    return { rowMin, rowMax, colMin, colMax };
  };

  const createPageRange = (): (number | string)[] => {
    const totalPages = Math.ceil(rows.length / paginationModel.pageSize);
    const current = currentPage;
    const range: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        range.push(i);
      }
    } else {
      if (current <= 3) {
        for (let i = 0; i <= 4; i++) range.push(i);
        range.push('...');
        range.push(totalPages - 1);
      } else if (current >= totalPages - 4) {
        range.push(0);
        range.push('...');
        for (let i = totalPages - 5; i < totalPages; i++) range.push(i);
      } else {
        range.push(0);
        range.push('...');
        for (let i = current - 1; i <= current + 1; i++) range.push(i);
        range.push('...');
        range.push(totalPages - 1);
      }
    }

    return range;
  };
 
  // Tab functionality to move to cell below
    const moveToNextCell = (rowIndex: number, colIndex: number) => {
      const isLastRow = rowIndex === rows.length - 1;
      const colKey = columns[colIndex].key;
      const columnName = headers[colIndex];

      const nextRow = isLastRow ? null : rowIndex + 1;

      if (nextRow !== null) {
        // ✅ FIRST: move cursor immediately
        setEditingCell({ row: nextRow, col: colKey });

        // ✅ Auto-fill DOOR- if needed
        const headerName = columnName.trim().toLowerCase().replace(/[\s_-]/g, '');
        const isDockDoorId = headerName === 'dockdoorid';

        if (isDockDoorId) {
    // Check if current DC requires lowercase door prefix
    const caseCheckDCs = Environment.getDockDoorCaseCheckDCList();
    const useLowerCase = selectedOrg && caseCheckDCs.length > 0 && 
      caseCheckDCs.some(dc => dc.toUpperCase() === selectedOrg.toUpperCase());
    const doorPrefix = useLowerCase ? 'door-' : 'DOOR-';          setRows(prevRows => {
            const newRows = [...prevRows];
            const currentValue = newRows[nextRow][colKey] || '';

            if (currentValue.trim() === '') {
              newRows[nextRow] = {
                ...newRows[nextRow],
                [colKey]: doorPrefix
              };
            }

            return newRows;
          });
        }
      } else {
        // Last row → exit editing
        setEditingCell(null);

        
      }
  };

  //Handle Dock Door autofill
  const handleDockDoorAutoFill = (rowIndex: number, colIndex: number) => {
    const headerName = headers[colIndex]
      ?.trim()
      .toLowerCase()
      .replace(/[\s_-]/g, '');

    const isDockDoorId = headerName === 'dockdoorid';
    if (!isDockDoorId) return;

    // Check if current DC requires lowercase door prefix
    const caseCheckDCs = Environment.getDockDoorCaseCheckDCList();
    const useLowerCase = selectedOrg && caseCheckDCs.length > 0 && 
      caseCheckDCs.some(dc => dc.toUpperCase() === selectedOrg.toUpperCase());
    const doorPrefix = useLowerCase ? 'door-' : 'DOOR-';

    setRows(prevRows => {
      const newRows = [...prevRows];
      const colKey = columns[colIndex].key;
      const currentValue = newRows[rowIndex][colKey] || '';

      // ✅ Only auto-fill if empty
      if (currentValue.trim() === '') {
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          [colKey]: doorPrefix
        };
      }

      return newRows;
    });
};


  // Event handlers
  const handleClick = (rowIndex: number, colKey: string) => {
    setEditingCell({ row: rowIndex, col: colKey });
    if (onCellClick) {
      const colIndex = columns.findIndex(c => c.key === colKey);
      if (colIndex !== -1) {
        onCellClick(rowIndex, headers[colIndex]);
      }
    }
  };

  const handleCellChange = (event: React.ChangeEvent<HTMLInputElement>, rowIndex: number, colKey: string) => {
    const newValue = event.target.value;
    
    // Check if this is a shipment ID column and if it's being cleared
    const colIndex = columns.findIndex(col => col.key === colKey);
    const headerName = headers[colIndex]?.trim().toLowerCase().replace(/[\s_-]/g, '');
    const isShipmentIdColumn = headerName === 'shipmentid' || headerName === 'shipment';
    const isBeingCleared = newValue.trim() === '';
    
    setRows(prevRows => {
      const newRows = [...prevRows];
      
      if (isShipmentIdColumn && isBeingCleared) {
        // Clear the entire row if shipment ID is being cleared
        const clearedRow: any = {};
        columns.forEach(column => {
          clearedRow[column.key] = '';
        });
        newRows[rowIndex] = clearedRow;
      } else {
        // Normal cell update
        newRows[rowIndex] = { ...newRows[rowIndex], [colKey]: newValue };
      }
      
      return newRows;
    });
    
    // If we cleared the row, clear all validation errors for this row
    if (isShipmentIdColumn && isBeingCleared) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        // Remove all validation errors for this row
        Object.keys(newErrors).forEach(errorKey => {
          if (errorKey.startsWith(`${rowIndex}-`)) {
            delete newErrors[errorKey];
          }
        });
        return newErrors;
      });
    } else {
      // Validate the cell normally
      const error = validateCell(rowIndex, colKey, newValue);
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        const errorKey = `${rowIndex}-${colKey}`;
        if (error) {
          newErrors[errorKey] = error;
        } else {
          delete newErrors[errorKey];
        }
        return newErrors;
      });
    }
    
    // Notify parent component about cell edit
    if (onCellEdit) {
      const colIndex = columns.findIndex(col => col.key === colKey);
      if (colIndex !== -1) {
        const columnName = headers[colIndex];
        onCellEdit(rowIndex, columnName, newValue);
      }
    }
  };



  const toggleRowSelection = (rowIndex: number) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    // Get all rows with data across ALL pages
    const allRowsWithData = rows
      .map((row, index) => ({
        index: index,
        hasData: columns.some(col => {
          const value = row[col.key] || '';
          return value.trim() !== '';
        })
      }))
      .filter(item => item.hasData)
      .map(item => item.index);
    
    const allSelected = allRowsWithData.length > 0 && 
                       allRowsWithData.every(index => selectedRows.has(index));
    
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all rows with data across all pages
        allRowsWithData.forEach(index => newSet.delete(index));
      } else {
        // Select all rows with data across all pages
        allRowsWithData.forEach(index => newSet.add(index));
      }
      return newSet;
    });
  };

  const isAllSelected = () => {
    // Check all rows with data across ALL pages
    const allRowsWithData = rows
      .map((row, index) => ({
        index: index,
        hasData: columns.some(col => {
          const value = row[col.key] || '';
          return value.trim() !== '';
        })
      }))
      .filter(item => item.hasData)
      .map(item => item.index);
    
    return allRowsWithData.length > 0 && 
           allRowsWithData.every(index => selectedRows.has(index));
  };

  const addRow = () => {
    const newRow: any = { tempKey: `row_${rows.length}` };
    columns.forEach(col => {
      // Don't set default values for reset status columns when adding empty rows
      newRow[col.key] = '';
    });
    setRows(prev => [...prev, newRow]);
  };

  const deleteSelected = () => {
    const indicesToDelete = Array.from(selectedRows).sort((a, b) => b - a);
    setRows(prev => {
      let newRows = [...prev];
      indicesToDelete.forEach(index => {
        newRows.splice(index, 1);
      });
      return newRows;
    });
    setSelectedRows(new Set());
  };

  //To clear table
  const clearTable = () => {
    // Reset to 25 blank rows
    const blankRows = Array.from({ length: 25 }, (_, index) => {
      const rowObject: any = { tempKey: `row_${index}` };
      columns.forEach(col => {
        // Don't set default values for reset status columns
        rowObject[col.key] = '';
      });
      return rowObject;
    });

    setRows(blankRows);
    setSelectedRows(new Set());
    // Clear validation errors to remove red highlighting
    setValidationErrors({});

    // Notify parent
    if (onDataChange) {
      onDataChange([]);
    }
  };


  const handlePageSizeChange = (newPageSize: number) => {
    setPaginationModel(prev => ({ ...prev, pageSize: newPageSize, page: 0 }));
  };

  const handleJumpToPage = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const pageNum = parseInt(jumpToPageValue);
      const maxPage = Math.ceil(rows.length / paginationModel.pageSize);
      
      if (isNaN(pageNum) || pageNum < 1 || pageNum > maxPage) {
        setJumpToPageError(`Please enter a valid page number (1-${maxPage})`);
        setTimeout(() => setJumpToPageError(null), 3000);
        return;
      }
      
      setPaginationModel(prev => ({ ...prev, page: pageNum - 1 }));
      setJumpToPageError(null);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTableElement>) => {
    event.preventDefault();
    
    // If currently editing a cell, paste into that cell only
    if (editingCell) {
      const pastedText = event.clipboardData.getData('text');
      // Remove any trailing newlines/whitespace for single cell paste
      const cleanedText = pastedText.replace(/[\r\n]+$/, '');
      
      setRows(prevRows => {
        const newRows = [...prevRows];
        newRows[editingCell.row] = { 
          ...newRows[editingCell.row], 
          [editingCell.col]: cleanedText 
        };
        return newRows;
      });
      
      // Validate the cell
      const error = validateCell(editingCell.row, editingCell.col, cleanedText);
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        const errorKey = `${editingCell.row}-${editingCell.col}`;
        if (error) {
          newErrors[errorKey] = error;
        } else {
          delete newErrors[errorKey];
        }
        return newErrors;
      });
      
      // Notify parent component about cell edit
      if (onCellEdit) {
        const colIndex = columns.findIndex(col => col.key === editingCell.col);
        if (colIndex !== -1) {
          const columnName = headers[colIndex];
          onCellEdit(editingCell.row, columnName, cleanedText);
        }
      }
      
      return;
    }
    
    // Regular multi-cell/multi-row paste
    const pastedText = event.clipboardData.getData('text');
    // Split by newline but remove empty trailing lines
    const pastedRows = pastedText
      .split('\n')
      .filter((row, index, array) => {
        // Keep non-empty rows, or empty rows that aren't at the end
        return row.trim() !== '' || index < array.length - 1;
      })
      .map(row => row.split('\t'));
    
    if (pastedRows.length === 0) return;
    
    const updatedRows = [...rows];
    let insertIndex = rows.length; 
    for (let i = rows.length - 1; i >= 0; i--) {
      const row = rows[i];
      const isEmpty = columns.every(col => !row[col.key] || row[col.key].toString().trim() === '');
      if (isEmpty) {
        insertIndex = i;
      } else {
        break; // Stop at first non-empty row
      }
    }
    
    pastedRows.forEach((pastedRow, index) => {
      const targetIndex = insertIndex + index;
      
      if (targetIndex < updatedRows.length) {
        columns.forEach((col, colIndex) => {
          updatedRows[targetIndex][col.key] = pastedRow[colIndex] || '';
        });
      } else {
        const newRow: any = { tempKey: `pasted_row_${targetIndex}` };
        columns.forEach((col, colIndex) => {
          newRow[col.key] = pastedRow[colIndex] || '';
        });
        updatedRows.push(newRow);
      }
    });
    
    setRows(updatedRows);
  };
  useEffect(() => {
    setJumpToPageValue((currentPage + 1).toString());
  }, [currentPage]);

  // Validate all cells when data, mandatory fields, or field types change
  useEffect(() => {
    if (rows.length > 0 && (mandatoryFields.length > 0 || Object.keys(fieldTypes).length > 0)) {
      // Add a small delay to allow status updates to complete before validation
      const validationTimer = setTimeout(() => {
        validateAllCells();
      }, 150);
      
      return () => clearTimeout(validationTimer);
    }
  }, [rows, mandatoryFields, fieldTypes, validateAllCells]);

  // Expose validation function to parent
  useEffect(() => {
    if (onValidate) {
      // Store the validation function reference
      (window as any)._validateTableData = validateAllCells;
    }
  }, [onValidate, validateAllCells]);

  // Notify parent when selected rows change
  useEffect(() => {
    if (onSelectedRowsChange) {
      onSelectedRowsChange(selectedRows);
    }
  }, [selectedRows, onSelectedRowsChange]);

  // Notify parent when validation errors change
  useEffect(() => {
    if (onValidationChange) {
      const hasErrors = Object.keys(validationErrors).length > 0;
      onValidationChange(hasErrors);
    }
  }, [validationErrors, onValidationChange]);

  return (
    <div>
      {/* Action Buttons at Top */}
      {actionButtons && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          {actionButtons}
        </div>
      )}
      
      {/* Table */}
      <div className="table-wrapper" style={{ width: '100%', maxHeight: maxHeight, overflowX: 'auto', overflowY: 'auto' }}>
        <table
          className="excel-table"
          ref={tableRef}
          onPaste={handlePaste}
          tabIndex={0}
          style={{ width: '100%', borderCollapse: 'collapse' }}
          onMouseUp={() => {
            setDragSelecting(false);
            if (dragStartCell && dragEndCell) {
              setSelectedCellRect(getSelectedCellRect());
            }
          }}
          onMouseLeave={() => {
            setDragSelecting(false);
            if (dragStartCell && dragEndCell) {
              setSelectedCellRect(getSelectedCellRect());
            }
          }}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
              e.preventDefault();
              const rect = selectedCellRect || getSelectedCellRect();
              if (rect) {
                const { rowMin, rowMax, colMin, colMax } = rect;
                const selectedRowsData = [];
                for (let r = rowMin; r <= rowMax; r++) {
                  const row = rows[r];
                  const selectedCols = columns.slice(colMin, colMax + 1);
                  selectedRowsData.push(selectedCols.map(col => row[col.key] ?? '').join('\t'));
                }
                const text = selectedRowsData.join('\n');
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text);
                } else {
                  const textarea = document.createElement('textarea');
                  textarea.value = text;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                }
                return;
              }
            }
          }}
        >
          <thead>
            <tr>
              {showEditControls && (
                <th style={{ width: '40px', border: '1px solid #ddd', padding: '8px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected()}
                    onChange={toggleSelectAll}
                    title="Select all rows with data across all pages"
                  />
                </th>
              )}
              <th style={{ width: '60px', border: '1px solid #ddd', padding: '8px', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                Sl #
              </th>
              {columns.map((col, index) => {
                const headerName = headers[index];
                const isMandatory = mandatoryFields.includes(headerName);
                
                               
                return (
                  <th key={col.key} style={{ border: '1px solid #ddd', padding: '8px', backgroundColor: '#f5f5f5', minWidth: '120px', textAlign: 'center' }}>
                    {col.name}
                    {isMandatory && <span style={{ color: 'red', fontWeight: 'bold', marginLeft: '4px' }}>*</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows
              .slice(
                paginationModel.page * paginationModel.pageSize,
                (paginationModel.page + 1) * paginationModel.pageSize
              )
              .map((row: any, rowIndex: number) => {
                const actualRowIndex = paginationModel.page * paginationModel.pageSize + rowIndex;
                return (
                  <tr key={row.tempKey || actualRowIndex}>
                    {showEditControls && (
                      <td style={{ border: '1px solid #ddd', padding: '4px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(actualRowIndex)}
                          onChange={() => toggleRowSelection(actualRowIndex)}
                        />
                      </td>
                    )}
                    <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>
                      {actualRowIndex + 1}
                    </td>
                    {columns.map((col, colIndex) => {
                      const rect = selectedCellRect || getSelectedCellRect();
                      const errorKey = `${actualRowIndex}-${col.key}`;
                      const hasError = validationErrors[errorKey];
                      
                      let dragCellHighlight = {};
                      if (rect && actualRowIndex >= rect.rowMin && actualRowIndex <= rect.rowMax && colIndex >= rect.colMin && colIndex <= rect.colMax) {
                        dragCellHighlight = {
                          backgroundColor: '#cce5ff',
                          border: '2px solid #339af0'
                        };
                      }
                      
                      // Error highlighting overrides drag selection
                      let cellStyle: React.CSSProperties = {
                        border: '1px solid #ddd',
                        padding: '8px',
                        cursor: 'pointer',
                        minWidth: '120px',
                        position: 'relative',
                        ...dragCellHighlight
                      };

                      const headerName = headers[colIndex];
                      if (nonEditableColumns.includes(headerName)) {
                        cellStyle.cursor = 'default';
                        cellStyle.backgroundColor = '#f8f9fa'; // A light grey to indicate non-editable
                      }
                      
                      if (hasError) {
                        cellStyle = {
                          ...cellStyle,
                          backgroundColor: '#fee',
                          border: '2px solid #f44',
                        };
                      }

                      const cellContent = (
                        <td
                          key={col.key}
                          onMouseDown={e => {
                            if (e.button === 0) {
                              setDragSelecting(true);
                              setDragStartCell({ row: actualRowIndex, col: colIndex });
                              setDragEndCell({ row: actualRowIndex, col: colIndex });
                              setSelectedCellRect(null);
                            }
                          }}
                          onMouseEnter={e => {
                            if (dragSelecting && dragStartCell) {
                              setDragEndCell({ row: actualRowIndex, col: colIndex });
                            }
                          }}
                          onClick={() => {
                            const headerName = headers[colIndex];
                            const disabled = isCellDisabled?.(actualRowIndex, headerName);
                            if (nonEditableColumns.includes(headerName) || disabled) {
                              return; // Do not enter edit mode for non-editable columns
                            }
                            const isStatusColumn = headerName.trim().toLowerCase() === 'status';
                            const isStopStatusColumn = headerName?.toLowerCase().replace(/[\s_-]/g, '') === 'stopstatus';
                            const isResetStatusColumn = headerName?.toLowerCase() === 'resetstatus';
                            
                            // Prevent clicking on Status and Stop Status columns (non-editable)
                            if (!isStatusColumn && !isStopStatusColumn) {
                              if (!isResetStatusColumn) {
                                // ✅ Auto-fill DOOR- only when clicking an empty DockDoorId cell
                                handleDockDoorAutoFill(actualRowIndex, colIndex);
                              }
                              // Allow clicking for ResetStatus and other editable columns
                              handleClick(actualRowIndex, col.key);
                            }
                          }}

                          style={cellStyle}
                        >
                          {editingCell && editingCell.row === actualRowIndex && editingCell.col === col.key ? (
                            // Check if this is the ResetStatus column
                            (() => {
                              const headerName = headers[colIndex]?.trim();
                              const disabled = isCellDisabled?.(actualRowIndex, headerName);
                              
                            if (disabled) {
                              
                              
                                cellStyle = {
    ...cellStyle,
    backgroundColor: '#f0f0f0',
    cursor: 'not-allowed'
  };


                                   return <span>{row[col.key] || ''}</span>;
                              }

                              const normalizedHeader = headerName?.toLowerCase().replace(/[\s_-]/g, '');
                              const isResetStatusColumn = normalizedHeader === 'resetstatus';
                              
                              if (isResetStatusColumn) {
                                // Check if Stop Status column has a value
                                const stopStatusColIndex = headers.findIndex(header => 
                                  header?.toLowerCase().replace(/[\s_-]/g, '') === 'stopstatus'
                                );
                                const stopStatusColKey = columns[stopStatusColIndex]?.key;
                                const stopStatusValue = stopStatusColKey ? row[stopStatusColKey] : '';
                                const hasValidStopStatus = stopStatusValue && 
                                  String(stopStatusValue).trim() !== '' && 
                                  String(stopStatusValue).trim() !== 'Error' && 
                                  String(stopStatusValue).trim().toLowerCase() !== 'unknown';
                                
                                // Show dropdown only when Stop Status has a valid value
                                if (!hasValidStopStatus) {
                                  // Show regular input field when conditions are not met
                                  return (
                                    <input
                                      type="text"
                                      value={row[col.key] || ''}
                                      readOnly
                                      placeholder="Requires valid Stop Status"
                                      style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '0.875rem',
                                        backgroundColor: '#f5f5f5',
                                        color: '#666'
                                      }}
                                    />
                                  );
                                }
                                
                                // Get the current reset status value
                                let resetStatusValue = row[col.key] || '';
                                
                                // Only set default value if the row has meaningful data (not just cleared)
                                const rowHasActualData = Object.entries(row).some(([key, value]) => {
                                  // Exclude tempKey and check if any column has meaningful data
                                  return key !== 'tempKey' && value && String(value).trim() !== '';
                                });
                                
                                if ((!resetStatusValue || resetStatusValue === '') && rowHasActualData) {
                                  resetStatusValue = 'LOADING IN PROGRESS';
                                  const updatedRows = [...rows];
                                  updatedRows[actualRowIndex][col.key] = 'LOADING IN PROGRESS';
                                  setRows(updatedRows);
                                  if (onDataChange) {
                                    const convertedData = updatedRows.map(row => 
                                      columns.map(column => String(row[column.key] || ''))
                                    );
                                    onDataChange(convertedData);
                                  }
                                }
                                
                                // Enable dropdown when both conditions are met
                                const isDisabled = false;
                                
                                return (
                                  <FormControl fullWidth size="small" disabled={isDisabled}>
                                    <Select
                                      value={resetStatusValue}
                                      onChange={(e) => {
                                        const newValue = e.target.value;
                                        const updatedRows = [...rows];
                                        updatedRows[actualRowIndex][col.key] = newValue;
                                        setRows(updatedRows);
                                        if (onDataChange) {
                                          const convertedData = updatedRows.map(row => 
                                            columns.map(column => String(row[column.key] || ''))
                                          );
                                          onDataChange(convertedData);
                                        }
                                      }}
                                      displayEmpty
                                      style={{ fontSize: '0.875rem' }}
                                    >
                                      <MenuItem value="OPEN">OPEN</MenuItem>
                                      <MenuItem value="DC ACCEPTED">DC ACCEPTED</MenuItem>
                                      <MenuItem value="LOADING IN PROGRESS">LOADING IN PROGRESS</MenuItem>
                                    </Select>
                                  </FormControl>
                                );
                              } else {
                                return (
                                  <input
                                    type="text"
                                    value={row[col.key] || ''}

                                    onBeforeInput={(e) => {
                                    const headerName = headers[colIndex]
                                      ?.trim()
                                      .toLowerCase()
                                      .replace(/[\s_-]/g, '');

                                    const isDockDoorId = headerName === 'dockdoorid';

                                    if (isDockDoorId) {
                                      const char = e.data;
                                      if (char && !/^[A-Za-z0-9-]$/.test(char)) {
                                        e.preventDefault();   // ✅ BLOCKS invalid character instantly
                                      }
                                    }
                                  }}

                                  onPaste={(e) => {
                                    const headerName = headers[colIndex]
                                      ?.trim()
                                      .toLowerCase()
                                      .replace(/[\s_-]/g, '');

                                    const isDockDoorId = headerName === 'dockdoorid';
                                    const isShipmentIdColumn = headerName === 'shipmentid' || headerName === 'shipment';

                                    console.log('🔍 Paste detected:', { 
                                      headerName, 
                                      isDockDoorId, 
                                      isShipmentIdColumn, 
                                      selectedOrg,
                                      selectedSubGroup 
                                    });

                                    if (isDockDoorId) {
                                      const text = e.clipboardData.getData("text");
                                      if (!/^[A-Za-z0-9-]*$/.test(text)) {
                                        e.preventDefault();   // ✅ BLOCKS invalid pasted text
                                      }
                                    }
                                    
                                    // Handle shipment ID paste - trigger debounced API call
                                    if (isShipmentIdColumn && selectedOrg) {
                                      console.log('✅ Shipment ID paste logic triggered');
                                      // Use setTimeout to ensure the paste value is processed first
                                      setTimeout(() => {
                                        const pastedValue = (e.target as HTMLInputElement).value?.trim();
                                        console.log('📋 Pasted value:', pastedValue);
                                        
                                        // Clear any existing timer for this row
                                        if (shipmentStatusTimerRef.current[actualRowIndex]) {
                                          clearTimeout(shipmentStatusTimerRef.current[actualRowIndex]);
                                        }
                                        
                                        // Cancel previous in-flight request for this row (if any)
                                        if (shipmentStatusAbortRef.current[actualRowIndex]) {
                                          shipmentStatusAbortRef.current[actualRowIndex]!.abort();
                                          shipmentStatusAbortRef.current[actualRowIndex] = null;
                                        }
                                        
                                        // Find stop status column
                                        const stopStatusColIndex = headers.findIndex(header => 
                                          header?.toLowerCase().replace(/[\s_-]/g, '') === 'stopstatus'
                                        );
                                        const stopStatusColKey = columns[stopStatusColIndex]?.key;
                                        
                                        if (!pastedValue || pastedValue.length === 0) {
                                          // Clear stop status if shipment ID is empty
                                          if (stopStatusColKey) {
                                            setRows(prevRows => {
                                              const newRows = [...prevRows];
                                              if (newRows[actualRowIndex]) {
                                                newRows[actualRowIndex][stopStatusColKey] = '';
                                              }
                                              return newRows;
                                            });
                                          }
                                          return;
                                        }
                                        
                                        if (pastedValue && pastedValue.length >= 10 && stopStatusColKey) {
                                          console.log('🚀 Starting API call for shipment ID:', pastedValue);
                                          // Bump request id for this row
                                          shipmentStatusReqIdRef.current[actualRowIndex] = 
                                            (shipmentStatusReqIdRef.current[actualRowIndex] ?? 0) + 1;
                                          
                                          // Set debounced timer (200ms like typing)
                                          shipmentStatusTimerRef.current[actualRowIndex] = setTimeout(async () => {
                                           const ctrl = new AbortController();
                                            shipmentStatusAbortRef.current[actualRowIndex] = ctrl;
                                            
                                            // Capture the request id at the time we scheduled this call
                                            const requestId = shipmentStatusReqIdRef.current[actualRowIndex] ?? 0;
                                            
                                            try {
                                              const stopStatus = await getStopStatus({
                                                dcCode: selectedOrg,
                                                shipmentIds: pastedValue
                                              });
                                              
                                             
                                              // Ignore if a newer request started while we awaited (stale response)
                                              if ((shipmentStatusReqIdRef.current[actualRowIndex] ?? 0) !== requestId) return;
                                              
                                              shipmentStatusAbortRef.current[actualRowIndex] = null;
                                              
                                              if (stopStatus && stopStatus.trim() !== '') {
                                                if (stopStatus.trim().toLowerCase() === 'unknown') {
                                                  // Unknown status - show popup with shipment ID
                                                  setRows(prevRows => {
                                                    const newRows = [...prevRows];
                                                    newRows[actualRowIndex][stopStatusColKey] = stopStatus;
                                                    return newRows;
                                                  });
                                                  setSnackbarMessage(`Invalid Shipment Id: ${pastedValue}`);
                                                  setSnackbarSeverity('error');
                                                  setShowSnackbar(true);
                                                  setValidationErrors(prev => ({
                                                    ...prev,
                                                    [`${actualRowIndex}-${col.key}`]: 'Invalid Shipment Id'
                                                  }));
                                                } else {
                                                  // Valid shipment ID - update stop status
                                                  setRows(prevRows => {
                                                    const newRows = [...prevRows];
                                                    newRows[actualRowIndex][stopStatusColKey] = stopStatus;
                                                    return newRows;
                                                  });
                                                  // Clear any existing error
                                                  setValidationErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors[`${actualRowIndex}-${col.key}`];
                                                    return newErrors;
                                                  });
                                                }
                                              } else {
                                                // Invalid shipment ID - show popup
                                                setSnackbarMessage(`Invalid Shipment Id: ${pastedValue}`);
                                                setSnackbarSeverity('error');
                                                setShowSnackbar(true);
                                                setValidationErrors(prev => ({
                                                  ...prev,
                                                  [`${actualRowIndex}-${col.key}`]: 'Invalid Shipment Id'
                                                }));
                                              }
                                            } catch (error: any) {
                                              console.error('❌ API Error:', error);
                                              // Ignore errors from aborted requests
                                              if (error?.name === 'AbortError') return;
                                              
                                              // Also ignore if a newer request started meanwhile
                                              if ((shipmentStatusReqIdRef.current[actualRowIndex] ?? 0) !== requestId) return;
                                              
                                              shipmentStatusAbortRef.current[actualRowIndex] = null;
                                              
                                              // Invalid shipment ID due to API error - show popup
                                              setSnackbarMessage(`Invalid Shipment Id: ${pastedValue}`);
                                              setSnackbarSeverity('error');
                                              setShowSnackbar(true);
                                              setValidationErrors(prev => ({
                                                ...prev,
                                                [`${actualRowIndex}-${col.key}`]: 'Invalid Shipment Id'
                                              }));
                                            }
                                          }, 200); // Wait 200ms after paste
                                        } else {
                                          console.log('❌ Conditions not met for API call:', {
                                            pastedValueLength: pastedValue?.length,
                                            stopStatusColKey,
                                            minLength: pastedValue && pastedValue.length >= 10
                                          });
                                        }
                                      }, 0); // Minimal delay to let paste complete
                                    } else {
                                      console.log('❌ Paste conditions not met:', {
                                        isShipmentIdColumn,
                                        selectedOrg
                                      });
                                    }
                                  }}

                                    onChange={(e) => {
                                      handleCellChange(e, actualRowIndex, col.key);
                                      
                                      // Check if this is a shipment ID column and implement debounced validation
                                      const headerName = headers[colIndex]?.trim().toLowerCase().replace(/[\s_-]/g, '');
                                      const isShipmentIdColumn = headerName === 'shipmentid' || headerName === 'shipment';
                                      const shipmentIdValue = e.target.value?.trim();
                                      
                                      if (isShipmentIdColumn && selectedOrg) {
                                        // Clear any existing timer for this row
                                        if (shipmentStatusTimerRef.current[actualRowIndex]) {
                                          clearTimeout(shipmentStatusTimerRef.current[actualRowIndex]);
                                        }
                                        
                                        // Cancel previous in-flight request for this row (if any)
                                        if (shipmentStatusAbortRef.current[actualRowIndex]) {
                                          shipmentStatusAbortRef.current[actualRowIndex]!.abort();
                                          shipmentStatusAbortRef.current[actualRowIndex] = null;
                                        }
                                        
                                        // Find stop status column
                                        const stopStatusColIndex = headers.findIndex(header => 
                                          header?.toLowerCase().replace(/[\s_-]/g, '') === 'stopstatus'
                                        );
                                        const stopStatusColKey = columns[stopStatusColIndex]?.key;
                                        
                                        if (!shipmentIdValue || shipmentIdValue.length === 0) {
                                          // Clear stop status if shipment ID is empty
                                          if (stopStatusColKey) {
                                            setRows(prevRows => {
                                              const newRows = [...prevRows];
                                              if (newRows[actualRowIndex]) {
                                                newRows[actualRowIndex][stopStatusColKey] = '';
                                              }
                                              return newRows;
                                            });
                                          }
                                          return;
                                        }
                                        
                                        if (shipmentIdValue && shipmentIdValue.length >= 10 && stopStatusColKey) {
                                          // Bump request id for this row
                                          shipmentStatusReqIdRef.current[actualRowIndex] = 
                                            (shipmentStatusReqIdRef.current[actualRowIndex] ?? 0) + 1;

                                          shipmentStatusTimerRef.current[actualRowIndex] = setTimeout(async () => {
                                            const ctrl = new AbortController();
                                            shipmentStatusAbortRef.current[actualRowIndex] = ctrl;
                                            const requestId = shipmentStatusReqIdRef.current[actualRowIndex] ?? 0;
                                            try {
                                              const stopStatus = await getStopStatus({
                                                dcCode: selectedOrg,
                                                shipmentIds: shipmentIdValue
                                              });                                              
                                              if ((shipmentStatusReqIdRef.current[actualRowIndex] ?? 0) !== requestId) return;
                                              
                                              shipmentStatusAbortRef.current[actualRowIndex] = null;                                              
                                              if (stopStatus && stopStatus.trim() !== '') {
                                                if (stopStatus.trim().toLowerCase() === 'unknown') {
                                                  setRows(prevRows => {
                                                    const newRows = [...prevRows];
                                                    newRows[actualRowIndex][stopStatusColKey] = stopStatus;
                                                    return newRows;
                                                  });
                                                  setSnackbarMessage(`Invalid Shipment Id: ${shipmentIdValue}`);
                                                  setSnackbarSeverity('error');
                                                  setShowSnackbar(true);
                                                  setValidationErrors(prev => ({
                                                    ...prev,
                                                    [`${actualRowIndex}-${col.key}`]: 'Invalid Shipment Id'
                                                  }));
                                                } else {
                                                  setRows(prevRows => {
                                                    const newRows = [...prevRows];
                                                    newRows[actualRowIndex][stopStatusColKey] = stopStatus;
                                                    return newRows;
                                                  });
                                                  setValidationErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors[`${actualRowIndex}-${col.key}`];
                                                    return newErrors;
                                                  });
                                                }
                                              } else {
                                                setSnackbarMessage(`Invalid Shipment Id: ${shipmentIdValue}`);
                                                setSnackbarSeverity('error');
                                                setShowSnackbar(true);
                                                setValidationErrors(prev => ({
                                                  ...prev,
                                                  [`${actualRowIndex}-${col.key}`]: 'Invalid Shipment Id'
                                                }));
                                              }
                                            } catch (error: any) {
                                              if (error?.name === 'AbortError') return;
                                              
                                              // Also ignore if a newer request started meanwhile
                                              if ((shipmentStatusReqIdRef.current[actualRowIndex] ?? 0) !== requestId) return;
                                              
                                              shipmentStatusAbortRef.current[actualRowIndex] = null;
                                              
                                              // Invalid shipment ID due to API error - show popup
                                              setSnackbarMessage(`Invalid Shipment Id: ${shipmentIdValue}`);
                                              setSnackbarSeverity('error');
                                              setShowSnackbar(true);
                                              setValidationErrors(prev => ({
                                                ...prev,
                                                [`${actualRowIndex}-${col.key}`]: 'Invalid Shipment Id'
                                              }));
                                            }
                                          }, 200); // Wait 200ms after user stops typing
                                        }
                                      }
                                    }}
                                    onBlur={() => {
                                      setEditingCell(null);
                                    }}

                                    onKeyDown={(e) => {
                                     if (e.key === 'Tab') {
                                        e.preventDefault();
                                        moveToNextCell(actualRowIndex, colIndex);
                                        return;
                                      }

                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        moveToNextCell(actualRowIndex, colIndex);
                                        return;
                                      }

                                      if (e.key === 'Escape') {
                                        setEditingCell(null);
                                      }
                                   }}
                                    autoFocus
                                    style={{
                                      width: '100%',
                                      border: 'none',
                                      outline: 'none',
                                      background: 'transparent'
                                    }}
                                  />
                                );
                              }
                            })()
                          ) : (
                            (() => {
                              const headerName = headers[colIndex]?.trim();
                              const normalizedHeader = headerName?.toLowerCase().replace(/[\s_-]/g, '');
                              const isResetStatusColumn = normalizedHeader === 'resetstatus';
                              const isStopStatusColumn = normalizedHeader === 'stopstatus';
                              
                              // Handle ResetStatus column with automatic dropdown when StopStatus has valid value
                              if (isResetStatusColumn) {
                                // Check if Stop Status column has a valid value
                                const stopStatusColIndex = headers.findIndex(header => 
                                  header?.toLowerCase().replace(/[\s_-]/g, '') === 'stopstatus'
                                );
                                const stopStatusColKey = columns[stopStatusColIndex]?.key;
                                const stopStatusValue = stopStatusColKey ? row[stopStatusColKey] : '';
                                const hasValidStopStatus = stopStatusValue && 
                                  String(stopStatusValue).trim() !== '' && 
                                  String(stopStatusValue).trim() !== 'Error' && 
                                  String(stopStatusValue).trim().toLowerCase() !== 'unknown';
                                
                                // Show dropdown when StopStatus has a valid value
                                if (hasValidStopStatus) {
                                  let resetStatusValue = row[col.key] || '';
                                  
                                  // Only set default value if the row has meaningful data (not just cleared)
                                  const rowHasActualData = Object.entries(row).some(([key, value]) => {
                                    // Exclude tempKey and check if any column has meaningful data
                                    return key !== 'tempKey' && value && String(value).trim() !== '';
                                  });
                                  
                                  if ((!row[col.key] || row[col.key] === '') && rowHasActualData) {
                                    resetStatusValue = 'LOADING IN PROGRESS';
                                    const updatedRows = [...rows];
                                    updatedRows[actualRowIndex][col.key] = 'LOADING IN PROGRESS';
                                    setRows(updatedRows);
                                    if (onDataChange) {
                                      const convertedData = updatedRows.map(row => 
                                        columns.map(column => String(row[column.key] || ''))
                                      );
                                      onDataChange(convertedData);
                                    }
                                  }
                                  
                                  return (
                                    <FormControl fullWidth size="small">
                                      <Select
                                        value={resetStatusValue}
                                        onChange={(e) => {
                                          const newValue = e.target.value;
                                          const updatedRows = [...rows];
                                          updatedRows[actualRowIndex][col.key] = newValue;
                                          setRows(updatedRows);
                                          if (onDataChange) {
                                            const convertedData = updatedRows.map(row => 
                                              columns.map(column => String(row[column.key] || ''))
                                            );
                                            onDataChange(convertedData);
                                          }
                                        }}
                                        displayEmpty
                                        style={{ fontSize: '0.875rem' }}
                                      >
                                        <MenuItem value="OPEN">OPEN</MenuItem>
                                        <MenuItem value="DC ACCEPTED">DC ACCEPTED</MenuItem>
                                        <MenuItem value="LOADING IN PROGRESS">LOADING IN PROGRESS</MenuItem>
                                      </Select>
                                    </FormControl>
                                  );
                                } else {
                                  // Show placeholder text when StopStatus doesn't have valid value
                                  return (
                                    <span style={{ 
                                      padding: '4px 8px', 
                                      fontSize: '0.875rem',
                                      color: '#999',
                                      fontStyle: 'italic'
                                    }}>
                                      {row[col.key] || ''}
                                    </span>
                                  );
                                }
                              } else if (isStopStatusColumn) {
                                // Handle StopStatus column display
                                const statusValue = row[col.key] || '';
                                const hasValidStatus = statusValue && statusValue !== 'Error' && statusValue !== 'Loading...';
                                
                                return (
                                  <span style={{ 
                                    padding: '4px 8px', 
                                    fontSize: '0.875rem',
                                    color: hasValidStatus ? '#000' : '#999',
                                    fontStyle: hasValidStatus ? 'normal' : 'italic'
                                  }}>
                                    {statusValue}
                                  </span>
                                );
                              } else {
                                return <span>{row[col.key] || ''}</span>;
                              }
                            })()
                          )}
                        </td>
                      );

                      return hasError ? (
                        <Tooltip key={col.key} title={hasError} arrow>
                          {cellContent}
                        </Tooltip>
                      ) : cellContent;
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Controls */}
      {showPaginationControls && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '38px' }}>
            {showEditControls && (
              <div style={{ display: 'flex', gap: '38px', whiteSpace: 'nowrap' }}>
                <Button onClick={addRow} disabled={isLoading}>
                  Add Row
                </Button>
                <Button onClick={clearTable} disabled={isLoading}>
                  Clear
                </Button>
                <Button onClick={deleteSelected} disabled={selectedRows.size === 0 || isLoading}>
                  Delete Row
                </Button>
              </div>
            )}
            
            <div style={{ marginLeft: showEditControls ? '24px' : '0', width: '270%' }}>Rows per page:</div>
            <Select
              value={paginationModel.pageSize}
              onChange={(event) => handlePageSizeChange(Number(event.target.value))}
              size="small"
              style={{ width: '70px' }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={75}>75</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
            
            <div style={{ marginLeft: '8px', width: '140%' }}>
              {rows.length === 0
                ? '0–0 of 0'
                : `${paginationModel.page * paginationModel.pageSize + 1}–${Math.min(
                  (paginationModel.page + 1) * paginationModel.pageSize,
                  rows.length
                )} of ${rows.length}`}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            
  {onReset && (
    <Tooltip title="Reset New Sequence">
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          if (onReset) {
            onReset();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (onReset) {
              onReset();
            }
          }
        }}
        style={{
          color: '#007bff',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        Reset
      </span>
    </Tooltip>
  )}

            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {/* Previous */}
              <div
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  backgroundColor: currentPage === 0 ? '#f5f5f5' : 'white',
                }}
                onClick={() => {
                  if (currentPage > 0) {
                    setPaginationModel((prev) => ({ ...prev, page: currentPage - 1 }));
                  }
                }}
              >
                &lt;
              </div>
              
              {/* Page Numbers */}
              {createPageRange().map((page, index) => (
                typeof page === 'number' ? (
                  <div
                    key={index}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: page === currentPage ? '#007bff' : 'white',
                      color: page === currentPage ? 'white' : 'black',
                    }}
                    onClick={() => setPaginationModel((prev) => ({ ...prev, page }))}
                  >
                    {page + 1}
                  </div>
                ) : (
                  <div key={index} style={{ padding: '4px 8px', color: '#999' }}>
                    ...
                  </div>
                )
              ))}

              {/* Next */}
              <div
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: (currentPage + 1) * paginationModel.pageSize >= rows.length ? 'not-allowed' : 'pointer',
                  backgroundColor: (currentPage + 1) * paginationModel.pageSize >= rows.length ? '#f5f5f5' : 'white',
                }}
                onClick={() => {
                  if ((currentPage + 1) * paginationModel.pageSize < rows.length) {
                    setPaginationModel((prev) => ({ ...prev, page: currentPage + 1 }));
                  }
                }}
              >
                &gt;
              </div>
            </div>
            
            {/* Jump to Page */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Jump to:</span>
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 1, max: Math.ceil(rows.length / paginationModel.pageSize) }}
                value={jumpToPageValue}
                onChange={e => setJumpToPageValue(e.target.value)}
                onKeyDown={handleJumpToPage}
                placeholder="Page #"
                style={{ width: '90px' }}
              />
            </div>
          </div>
        </Box>
      )}

      {/* Error Snackbar */}
      {jumpToPageError && (
        <Snackbar
          open={true}
          onClose={() => setJumpToPageError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={3000}
        >
          <Alert severity="error" onClose={() => setJumpToPageError(null)}>
            {jumpToPageError}
          </Alert>
        </Snackbar>
      )}

      {/* Validation Error Snackbar */}
      {showValidationAlert && (
        <Snackbar
          open={true}
          onClose={() => setShowValidationAlert(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={5000}
        >
          <Alert severity="warning" onClose={() => setShowValidationAlert(false)}>
            {validationAlertMessage}
          </Alert>
        </Snackbar>
      )}

      {/* Invalid Shipment ID Snackbar */}
      {showSnackbar && (
        <Snackbar
          open={true}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={4000}
        >
          <Alert severity={snackbarSeverity} onClose={() => setShowSnackbar(false)}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default EditableTable;